import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
interface PresignedResponse {
    exists: boolean;
    valid: boolean;
    file_size: number | null;
    status?: number;
    statusText?: string;
    dna_ratio_percent?: number;
    dna_ratio?: number ;
    atom_total?: number;           
    nucleotide_atoms?: number;    
    protein_atoms?: number;       
    warnings?: string[];          
    rdkit?: {
        enabled: boolean;
        skipped: boolean;
        reason: string | null;
    }
}

export interface iMoleculeCheckState {
    group_name: string | null;
    library_name: string | null;
    file_name: string | null;
    file_ext: string | null;
    file_type: string | null;
    isLoading: boolean;
    message: string | null;
    response: PresignedResponse;
    target_name : string | null;
    off_target_name : string | null;
    // 각 타입별로 응답을 분리
    drugResponse: PresignedResponse | null;
    targetResponse: PresignedResponse | null;
    assayResponse: PresignedResponse | null;
    // API 호출 상태 추가
    isSuccess: boolean;
    isError: boolean;
    // 개별 로딩 상태 추가 (선택사항)
    isDrugLoading: boolean;
    isTargetLoading: boolean;
    isAssayLoading: boolean;
    dna_ratio_percent : number | null;
    targetFilePutUpload? : boolean;
    offTargetFilePutUpload? : boolean;
}

const initialResponse: PresignedResponse = {
    exists: false,
    valid: false,
    file_size: null,
    status: undefined,
    statusText: undefined,
    dna_ratio_percent: null,
    dna_ratio : null,
};

const initialState: iMoleculeCheckState = {
    group_name: null,
    library_name: null,
    file_name: null,
    file_ext: null,
    file_type: null,
    isLoading: false,
    message: "",
    response: initialResponse,
    target_name : null,
    off_target_name : null,
    drugResponse: null,
    targetResponse: null,
    assayResponse: null,
    isSuccess: false,
    isError: false,
    isDrugLoading: false,
    isTargetLoading: false,
    isAssayLoading: false,
    dna_ratio_percent : null,
};

// Molecule Upload API 호출
export const uploadMoleculeCheckAPI = createAsyncThunk(
    "MOLECULE_UPLOAD_PRESIGNED",
    async (params: { 
        group_name: string; 
        library_name: string; 
        file_name: string; 
        file_ext: string;
        molecule_type?: string; // 'drug', 'target', 'assay' 
        workflow_name : String;
        target_name?: string;
        off_target_name?: string;
    }, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/upload/molecule-check/';
            
            // 요청 데이터 구성
            const requestData: any = {
                group_name: params.group_name,
                library_name: params.library_name,
                file_name: params.file_name,
                workflow_name : params.workflow_name,
                file_ext: params.file_ext,
                molecule_type : params.molecule_type
            };
            
            // molecule_type이 'target'이고 target_name이 제공된 경우 추가
            if (params.molecule_type === 'target' && params.target_name !== undefined) {
                requestData.target_name = params.target_name;
            }

            // molecule_type이 'off-target'이고 off_target_name이 제공된 경우 추가
            if (params.molecule_type === 'off-target' && params.off_target_name !== undefined) {
                requestData.off_target_name = params.off_target_name;
            }
            const response = await axiosInstance.post(
                `${djangoEndpoint}${pathParam}`, 
                requestData,
                {
                    withCredentials: true,
                }
            );
            
            return {
                ...response.data,  
                status: response.status,
                statusText: response.statusText,
                dna_ratio_percent: response.data.dna_ratio_percent ,
                dna_ratio: response.data.dna_ratio ,
                group_name: params.group_name,
                library_name: params.library_name,
                file_name: params.file_name,
                file_ext: params.file_ext,
                workflow_name: params.workflow_name,
                molecule_type: params.molecule_type || 'unknown',
            };
        } catch (error) {
            console.error('uploadMoleculeCheckAPI error:', error);
            
            // 에러 상태 코드도 함께 반환
            const errorStatus = error.response?.status || 500;
            const errorMessage = error.response?.data || error.message;
            
            return rejectWithValue({
                status: errorStatus,
                message: errorMessage,
                ...params // 요청 파라미터도 함께 반환
            });
        }
    }
);

// 파일 타입을 결정하는 헬퍼 함수
const determineFileType = (fileExt: string, explicitFileType?: string): string => {
    // 명시적으로 file_type이 제공된 경우 우선 사용
    if (explicitFileType && ['drug', 'target', 'assay'].includes(explicitFileType)) {
        return explicitFileType;
    }
    
    // fileExt가 유효하지 않은 경우 처리
    if (!fileExt || typeof fileExt !== 'string') {
        console.warn('Invalid file extension:', fileExt);
        return 'unknown';
    }
    
    // 파일 확장자에 따른 자동 분류
    const ext = fileExt.toLowerCase().replace(/^\./, '');
    
    // Drug 파일 확장자
    if (['sdf', 'mol'].includes(ext)) {
        return 'drug';
    }
    
    // Target 파일 확장자  
    if (['pdb'].includes(ext)) {
        return 'target';
    }
    
    // Assay 파일 확장자
    if (['csv', 'xlsx', 'xls', 'tsv'].includes(ext)) {
        return 'assay';
    }
    
    console.warn('Unknown file extension:', ext);
    return 'unknown';
};

// Molecule Upload Slice
export const uploadMoleculeCheckSlice = createSlice({
  name: "uploadMoleculeCheck",
  initialState,
  reducers: {
    // 전체 초기화 (신중하게 사용)
    resetMoleculeCheck: (state) => {
      Object.assign(state, initialState);
    },
    // 개별 파일 응답만 리셋
    resetDrugResponse: (state) => {
      state.drugResponse = null;
      state.isDrugLoading = false;
    },
    // uploadMoleculeCheckSlice.ts
    resetTargetResponse: (state) => {
      state.targetResponse = null;
      state.isTargetLoading = false;
      state.isLoading = false; // 이거 추가!
      state.isError = false; // 에러도 초기화
      state.message = ""; // 메시지도 초기화
    },
    resetAssayResponse: (state) => {
      state.assayResponse = null;
      state.isAssayLoading = false;
    },
    // 에러 상태만 초기화
    clearError: (state) => {
      state.isError = false;
      state.message = "";
    },
    resetDnaPercent: (state) => {
      // dna_ratio는 주로 target 파일에서 나오므로
      state.dna_ratio_percent = null;
      state.targetResponse = null; // target response 전체 초기화
      state.isTargetLoading = false;

      // response 객체의 dna 관련 필드만 초기화
      state.response = {
        ...state.response,
        dna_ratio_percent: null,
        dna_ratio: null,
      };
    },
    setPutTargetFileUpload: (state, action) => {
      state.targetFilePutUpload = action.payload.targetFilePutUpload;
    },
    setPutOffTargetFileUpload: (state, action) => {
      state.offTargetFilePutUpload = action.payload.offTargetFilePutUpload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadMoleculeCheckAPI.pending, (state, action) => {
        // 파일 타입에 따라 개별 로딩 상태 관리
        const fileType = determineFileType(
          action.meta.arg.file_ext || "",
          action.meta.arg.molecule_type
        );

        // 전체 로딩 상태 설정
        state.isLoading = true;
        state.isError = false;
        state.message = "";

        // 개별 파일 타입별 로딩 상태 설정
        if (fileType === "drug") {
          state.isDrugLoading = true;
        } else if (fileType === "target") {
          state.isTargetLoading = true;
        } else if (fileType === "assay") {
          state.isAssayLoading = true;
        }
      })
      .addCase(uploadMoleculeCheckAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;

        // 공통 응답 정보 업데이트
        state.response = {
          exists: action.payload.exists,
          valid: action.payload.valid,
          file_size: action.payload.file_size,
          status: action.payload.status,
          statusText: action.payload.statusText,
          dna_ratio_percent: action.payload.dna_ratio_percent,
          dna_ratio: action.payload.dna_ratio,
          atom_total: action.payload.atom_total, // 추가
          nucleotide_atoms: action.payload.nucleotide_atoms, // 추가
          protein_atoms: action.payload.protein_atoms, // 추가
          warnings: action.payload.warnings, // 추가
          rdkit: action.payload.rdkit, // 추가
        };

        const fileType = determineFileType(
          action.payload.file_ext || "",
          action.payload.molecule_type
        );

        // 응답 데이터 생성 - 여기도 추가!
        const responseData: PresignedResponse = {
          exists: action.payload.exists,
          valid: action.payload.valid,
          file_size: action.payload.file_size,
          status: action.payload.status,
          statusText: action.payload.statusText,
          dna_ratio_percent: action.payload.dna_ratio_percent ?? null, // ?? null 추가
          dna_ratio: action.payload.dna_ratio ?? null, // ?? null 추가
          atom_total: action.payload.atom_total ?? undefined, // ?? undefined
          nucleotide_atoms: action.payload.nucleotide_atoms ?? undefined,
          protein_atoms: action.payload.protein_atoms ?? undefined,
          warnings: action.payload.warnings ?? undefined,
          rdkit: action.payload.rdkit ?? undefined,
        };

        // 파일 타입별로 해당 응답만 업데이트
        if (fileType === "drug") {
          state.drugResponse = responseData;
          state.isDrugLoading = false;
        } else if (fileType === "target") {
          state.targetResponse = responseData;
          state.isTargetLoading = false;
        } else if (fileType === "assay") {
          state.assayResponse = responseData;
          state.isAssayLoading = false;
        }

        state.group_name = action.payload.group_name;
        state.library_name = action.payload.library_name;
        state.file_name = action.payload.file_name;
        state.file_ext = action.payload.file_ext;
        state.file_type = fileType;
      })
      .addCase(uploadMoleculeCheckAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;

        console.error("uploadMoleculeCheckAPI rejected:", action.payload);

        const errorPayload = action.payload as any;
        state.message = errorPayload?.message || "Upload failed";

        // 에러 발생한 파일 타입 확인
        const fileType = determineFileType(
          errorPayload?.file_ext || "",
          errorPayload?.molecule_type
        );

        // 개별 로딩 상태만 해제
        if (fileType === "drug") {
          state.isDrugLoading = false;
        } else if (fileType === "target") {
          state.isTargetLoading = false;
        } else if (fileType === "assay") {
          state.isAssayLoading = false;
        }

        // 공통 에러 응답 (다른 상태들은 유지)
        state.response = {
          exists: false,
          valid: false,
          file_size: null,
          status: errorPayload?.status || 500,
          statusText: "Error",
          dna_ratio_percent: null,
          dna_ratio: null,
        };

        // 요청 정보는 에러 시에도 저장
        if (errorPayload) {
          state.group_name = errorPayload.group_name;
          state.library_name = errorPayload.library_name;
          state.file_name = errorPayload.file_name;
          state.file_ext = errorPayload.file_ext;
          state.file_type = fileType;
        }
      });
  },
});

export const { 
    resetMoleculeCheck, 
    resetDrugResponse, 
    resetTargetResponse, 
    resetAssayResponse,
    resetDnaPercent,
    setPutTargetFileUpload,
    setPutOffTargetFileUpload,
    clearError
} = uploadMoleculeCheckSlice.actions;

export default uploadMoleculeCheckSlice.reducer;